import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2Icon } from 'lucide-react'
import { Slot } from '@/registry/reapit/lib/slot'

import { cn } from '@/registry/reapit/lib/utils'

/*
 * Button — DS-aligned implementation (Phase 3A)
 *
 * DS source: https://design.reapit.com.au — Button (pageId 8403092)
 *
 * Variant mapping:
 *   primary   — DS Primary: filled, brand action colour (fill-action-dark = purple-500)
 *   secondary — DS Secondary: neutral fill (fill-neutral-lightest), no border
 *   outline   — Reapit extension (not in DS). Visually: bordered, transparent bg.
 *               Kept for backwards-compatibility; maps closest to a bordered secondary.
 *   tertiary  — DS Tertiary: ghost/transparent, low-emphasis
 *
 * Size mapping:
 *   small  — DS Small:  h-8  (32px), px-3
 *   medium — DS Medium: h-9  (36px), px-4  [default]
 *   large  — DS Large:  h-10 (40px), px-6
 *   xs     — Reapit extension: h-6 (24px), compact UI only
 *   icon, icon-xs, icon-sm, icon-lg — Reapit extensions: square icon-only variants
 *
 * Icon default size:
 *   Buttons use size-5 (20px = DS icon-md) per Principal decision (plan/04).
 *   The library-wide default is size-4; the override is applied per-component here.
 *
 * Hover colours:
 *   primary   hover → bg-purple-600 (#4844DA = fill-action-dark one stop darker)
 *   secondary hover → bg-neutral-100 (fill-neutral-light, one stop darker than neutral-050)
 *   outline   hover → bg-accent (fill-action-lightest = purple-050) — matches DS tertiary hover
 *   tertiary  hover → bg-accent (fill-action-lightest = purple-050) — DS-specified
 *
 * Destructive modifier:
 *   Uses fill-error-dark (red-500 = --destructive) as per DS "destructive" style property.
 *
 * Focus ring:
 *   Uses var(--ring) which resolves to var(--border-focus) = purple-300 (#7E9BFA). Correct per DS.
 *
 * Disabled:
 *   opacity-50 on the element. DS specifies subdued but doesn't mandate a specific colour swap.
 */

const buttonVariants = cva(
  // Base — layout, typography, transitions, focus, disabled, invalid
  "inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-all outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5",
  {
    variants: {
      variant: {
        // DS Primary — filled, brand action colour
        primary: 'bg-primary text-primary-foreground shadow-xs hover:bg-purple-600 dark:hover:bg-purple-400',
        // DS Secondary — neutral fill, no border; hover darkens to neutral-100
        secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-neutral-100 dark:hover:bg-secondary/80',
        // Reapit extension — outlined, transparent bg; not in DS but kept for backwards-compat
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:border-neutral-600 dark:bg-input/30 dark:hover:bg-input/50',
        // DS Tertiary — ghost/transparent, lowest emphasis; hover = fill-action-lightest
        tertiary: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
      },
      size: {
        // DS sizes
        medium: 'h-9 px-4 py-2 has-[>svg]:px-3',
        small: 'h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5',
        large: 'h-10 rounded-lg px-6 has-[>svg]:px-4',
        // Reapit extensions
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        icon: 'size-9',
        'icon-xs': "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
      // Destructive is a style modifier, not a standalone variant — applied via compoundVariants
      destructive: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      // Primary destructive — filled red (fill-error-dark)
      {
        variant: 'primary',
        destructive: true,
        className:
          'bg-destructive text-white shadow-xs hover:bg-red-600 focus-visible:ring-destructive/20 dark:hover:bg-red-400',
      },
      // Secondary destructive — light red fill, red text
      {
        variant: 'secondary',
        destructive: true,
        className: 'bg-destructive/10 text-destructive hover:bg-destructive/20 dark:hover:bg-destructive/30',
      },
      // Tertiary destructive — red text, red hover fill
      {
        variant: 'tertiary',
        destructive: true,
        className: 'text-destructive hover:bg-destructive/10 dark:hover:bg-destructive/20',
      },
      // Outline destructive — red border, red text, red hover fill
      {
        variant: 'outline',
        destructive: true,
        className: 'border-destructive text-destructive hover:bg-destructive/10 dark:hover:bg-destructive/20',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'medium',
      destructive: false,
    },
  },
)

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace Button {
  export interface Props extends React.ComponentProps<'button'> {
    /** DS variants: primary | secondary | tertiary. "outline" is a Reapit extension. */
    variant?: 'primary' | 'secondary' | 'outline' | 'tertiary'
    /** DS sizes: medium (default) | small | large. xs / icon-* are Reapit extensions. */
    size?: 'medium' | 'small' | 'large' | 'xs' | 'icon' | 'icon-xs' | 'icon-sm' | 'icon-lg'
    /** Render as a child element using Slot (polymorphic). */
    asChild?: boolean
    /** Busy/loading state — shows spinner, prevents interaction. DS "Busy" state. */
    loading?: boolean
    /** Destructive style modifier — applies error-role colours. DS "Destructive" style. */
    destructive?: boolean
  }
}

function Button({
  className,
  variant = 'primary',
  size = 'medium',
  asChild = false,
  loading = false,
  destructive = false,
  disabled,
  children,
  ...props
}: Button.Props) {
  const buttonClassName = cn(
    buttonVariants({ variant, size, destructive, className }),
    loading && 'pointer-events-none',
  )

  if (asChild) {
    return (
      <Slot.Root data-slot="button" data-variant={variant} data-size={size} className={buttonClassName} {...props}>
        {children}
      </Slot.Root>
    )
  }

  return (
    <button
      data-slot="button"
      data-variant={variant}
      data-size={size}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      className={buttonClassName}
      {...props}
    >
      {loading && <Loader2Icon className="animate-spin" />}
      {children}
    </button>
  )
}

export { Button, buttonVariants }
